import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { dateGreaterThanEqualValidator } from '@shared/validators/date-greater-than-equal.validator';
import { lengthEqualTo } from '@shared/validators/length-equal-to';

/**
 * A utility class providing custom validator wrappers with optional error messages.
 *
 * Each static method returns a `ValidatorFn` that attaches a `message` to the validation error
 * if validation fails. This allows form errors to include custom user-friendly error messages.
 */
export class Validator {
  /**
   * Wraps a base validator function and attaches a custom error message if validation fails.
   *
   * @param baseValidator - The Angular validator function to wrap.
   * @param message - Optional error message to include in the returned validation error.
   * @returns A `ValidatorFn` that returns the base error plus a `message` property if validation fails.
   */
  private static createValidator(
    baseValidator: ValidatorFn,
    message?: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const error = baseValidator(control);
      return error ? { ...error, message } : null;
    };
  }

  /**
   * Validates that the control is not empty.
   *
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for required validation.
   */
  static required(message?: string): ValidatorFn {
    return this.createValidator(Validators.required, message);
  }

  /**
   * Validates that the control's value is true.
   *
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for requiredTrue validation.
   */
  static requiredTrue(message?: string): ValidatorFn {
    return this.createValidator(Validators.requiredTrue, message);
  }

  /**
   * Validates that the control's numeric value is greater than or equal to a given minimum.
   *
   * @param value - The minimum numeric value.
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for min validation.
   */
  static min(value: number, message?: string): ValidatorFn {
    return this.createValidator(Validators.min(value), message);
  }

  /**
   * Validates that the control's numeric value is less than or equal to a given maximum.
   *
   * @param value - The maximum numeric value.
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for max validation.
   */
  static max(value: number, message?: string): ValidatorFn {
    return this.createValidator(Validators.max(value), message);
  }

  /**
   * Validates that the control's string length is at least the specified minimum length.
   *
   * @param length - Minimum required length.
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for minLength validation.
   */
  static minLength(length: number, message?: string): ValidatorFn {
    return this.createValidator(Validators.minLength(length), message);
  }

  /**
   * Validates that the control's string length does not exceed the specified maximum length.
   *
   * @param length - Maximum allowed length.
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for maxLength validation.
   */
  static maxLength(length: number, message?: string): ValidatorFn {
    return this.createValidator(Validators.maxLength(length), message);
  }

  /**
   * Validates that the control contains a valid email address.
   *
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for email validation.
   */
  static email(message?: string): ValidatorFn {
    return this.createValidator(Validators.email, message);
  }

  /**
   * Validates that the control's value matches a specified pattern.
   *
   * @param regex - A string or RegExp pattern to match.
   * @param message - Optional error message.
   * @returns A `ValidatorFn` for pattern validation.
   */
  static pattern(regex: string | RegExp, message?: string): ValidatorFn {
    return this.createValidator(Validators.pattern(regex), message);
  }

  /**
   * Validates that the control's date value is greater than or equal to today's date.
   *
   * @param message - Optional error message.
   * @param minDateOrOffset - Optional offset from today's date (default: 0 days).
   * @returns A `ValidatorFn` for dateGreaterThanEqual validation.
   */
  static dateGreaterThanOrEqual(
    message?: string,
    minDateOrOffset: Date | number = 0
  ): ValidatorFn {
    return this.createValidator(dateGreaterThanEqualValidator(minDateOrOffset), message);
  }



  static lengthEqualTo(length: number | number[], message?: string): ValidatorFn {
    return this.createValidator(lengthEqualTo(length), message);
  }
}
